import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Trading → Dollar Cost Average → Dollar Cost Average.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Trading
 * (version 1, activated 2026-10-04): screens "Dollar Cost Average Account
 * Selection" (sleeve table + "Prohibited DCA Management Styles" button) and
 * "Dollar Cost Average" (the c:prosperityDollarCostAverage panel). The
 * panel's own field labels are not in the flow metadata, so this guide
 * describes what it collects (from its outputs: total amount, tranches,
 * schedule, start / end dates, instructions) without quoting labels. The
 * account filter is FinancialAccount.Do_Not_DCA__c. Shared chrome and policy
 * copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Trading',
    category: 'Dollar Cost Average',
    subCategory: 'Dollar Cost Average (the only choice)',
    subject: subjectRow('Dollar cost average - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
  }),
  {
    title: 'Pick the account to dollar cost average into',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The first <strong>Dollar Cost Average</strong> screen says <em>Please select the account you would
          like to Dollar Cost Average into.</em> Pick one row in the <strong>Select Account (Dollar Cost
          Average)</strong> table - it is required. Columns are Account Number, Account Details, Model,
          Management Style, Cash Balance and Balance, largest balance first, with a search bar above.
        </p>
        <p style={{ margin: 0 }}>
          Some management styles do not allow dollar cost averaging, so their accounts are left out of the
          table. The <strong>Prohibited DCA Management Styles</strong> button under the table shows the full
          list. Click Next.
        </p>
      </>
    ),
  },
  {
    title: 'Set up the schedule',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The second <strong>Dollar Cost Average</strong> screen holds Prosperity&rsquo;s dollar cost average
          panel, which is required. It collects:
        </p>
        <Bullets items={[
          'The total amount to dollar cost average, and the number of tranches. FSC divides the total by the tranches (to the cent) for the amount per tranche.',
          'The frequency and pattern - for example weekly on chosen weekdays, or monthly on a day of the month or an nth weekday - and how often it repeats.',
          'The start date and end date.',
          'Your instructions for Trading. These become the case description.',
        ]} />
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the panel - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

const QUESTIONS = [
  {
    screen: 'Dollar Cost Average Account Selection',
    q: 'Select Account (Dollar Cost Average)',
    when: 'Always',
    note: 'Single select, required. Lists sleeves only - see the trouble section if an account is missing.',
  },
  {
    q: 'Prohibited DCA Management Styles',
    when: 'A button under the table',
    note: 'Press it to show "Management Styles where Dollar Cost Averaging is prohibited:" - the styles whose accounts the table leaves out.',
  },
  {
    screen: 'Dollar Cost Average',
    q: 'The dollar cost average panel',
    when: 'Always',
    note: 'Required. Total amount, number of tranches, frequency and pattern, start and end dates, and instructions - see step 3 above.',
  },
  {
    q: 'Acknowledgement',
    when: 'Always, under the notice',
    options: ['I have read, understand, and agree to the above information'],
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Trading', 'Dollar Cost Average'),
  troubleSleeveRows('This table only lists active sleeves with a balance over $1, and leaves out any marked Do Not DCA: sleeves whose management style prohibits dollar cost averaging (the button lists them), client directed sleeves, cancelled sleeves ending in CNX, cash flow sleeves, sleeves with no management style, and the custodial account as a whole. If an account is not showing that should be, the screen asks you to contact the Trading Team (tradingops@prosperitycapitaladvisors.com).'),
  {
    h: 'The summary on the case looks wrong',
    p: [
      'Check the total, the number of tranches and the dates in the panel. The case summary is built from them, so a wrong figure there means a wrong figure in the panel - cancel and start again rather than editing around it.',
    ],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function DollarCostAverage() {
  return (
    <GuidePage
      title="Dollar Cost Average Requests"
      icon="calendar-range"
      path="Trading → Dollar Cost Average → Dollar Cost Average"
      minutes={4}
      intro="How to ask the Prosperity Trading team to dollar cost average into an account on a schedule - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The schedule is set once, on the form, and drives the recurring work.</Para>
        <Bullets items={[
          <><strong>The account and sleeve</strong> to dollar cost average into. You pick it from a list, so you do not need account numbers memorised.</>,
          <><strong>The total amount</strong> and <strong>the number of tranches</strong> to split it into.</>,
          <><strong>The schedule</strong> - how often (weekly or monthly, on which day), the start date and the end date.</>,
          <><strong>Any instructions for Trading</strong>, such as what to buy with each tranche.</>,
        ]} />
        <Callout>
          Accounts in some management styles cannot be dollar cost averaged and will not appear in the account
          table. Check the <strong>Prohibited DCA Management Styles</strong> list on the form if you are unsure.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why an account you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext
        team="trading"
        before={(
          <Callout>
            <strong>Recurring tasks.</strong> The case is set to create recurring Trading tasks (Dollar Cost
            Average) on the frequency, start date and end date you entered. FSC also writes a one-line summary
            onto the case in this pattern: &ldquo;Dollar Cost Average [total] over [n] tranches ([amount] per
            Tranche), monthly on the [day], starting [start date] and ending [end date].&rdquo; Check it after
            you submit.
          </Callout>
        )}
      />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not investment advice, and it does not describe
        whether dollar cost averaging is suitable for the client.
      </Footnote>
    </GuidePage>
  );
}
